import React, { useContext, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import Seo from '../components/Seo';

const PricingPage = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [tab, setTab] = useState('offers'); // 'offers' | 'consumption'
  const navigate = useNavigate();

  const handleSubscribeClick = () => {
    if (isAuthenticated) {
      navigate('/subscription');
    } else {
      navigate('/login');
    }
  };

  const planKey = (user?.subscription?.type) || 'free';
  const quotas = useMemo(() => ({
    free: { suggestions: 12, recipes: 3, iterations: 3, questions: 3 },
    premium: { suggestions: 120, recipes: 30, iterations: 30, questions: 30 },
    unlimited: { suggestions: Infinity, recipes: Infinity, iterations: Infinity, questions: Infinity },
  }), []);

  const usage = user?.usage || { suggestions: 0, recipes: 0, iterations: 0, questions: 0 };
  const current = quotas[planKey] || quotas.free;

  const Progress = ({ label, used, max }) => {
    if (max === Infinity) {
      return (
        <div className="text-sm text-gray-700 flex items-center justify-between">
          <span>{label}</span>
          <span>Illimité</span>
        </div>
      );
    }
    const clamped = Math.max(0, Math.min(used, max));
    const pct = Math.round((clamped / max) * 100);
    return (
      <div className="space-y-1">
        <div className="text-sm text-gray-700 flex items-center justify-between">
          <span>{label}</span>
          <span>{clamped} / {max}</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-2 bg-[#87A96B]" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  return (
    <>
      <Seo
        title="Tarifs"
        description="Comparez les offres YAQAM et choisissez la formule qui correspond à vos besoins pour planifier vos repas."
        breadcrumb
      />
      <div className="max-w-5xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-[#2C3E50]">Tarifs</h1>

      {/* Tabs */}
      <div className="mb-6 flex gap-2">
        <button onClick={()=>setTab('offers')} className={`px-3 py-2 rounded-lg text-sm ${tab==='offers' ? 'bg-[#2C3E50] text-white' : 'bg-gray-100 text-[#2C3E50] hover:bg-gray-200'}`}>Offres</button>
        <button onClick={()=>setTab('consumption')} className={`px-3 py-2 rounded-lg text-sm ${tab==='consumption' ? 'bg-[#2C3E50] text-white' : 'bg-gray-100 text-[#2C3E50] hover:bg-gray-200'}`}>Consommation</button>
      </div>

      {tab==='consumption' ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          {isAuthenticated && user ? (
            <>
              <h2 className="text-xl font-semibold mb-4 text-[#2C3E50]">Votre plan et consommation</h2>
              <div className="text-sm text-gray-700 mb-4">
                Plan actuel: <span className="capitalize font-medium">{planKey}</span>
                {user.subscription?.expiresAt && planKey !== 'unlimited' && (
                  <span> • Renouvellement le {new Date(user.subscription.expiresAt).toLocaleDateString()}</span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Progress label="Suggestions" used={usage.suggestions || 0} max={current.suggestions} />
                <Progress label="Recettes" used={usage.recipes || 0} max={current.recipes} />
                <Progress label="Itérations" used={usage.iterations || 0} max={current.iterations} />
                <Progress label="Questions YAQAM" used={usage.questions || 0} max={current.questions} />
              </div>
              {user.usage?.weekStart && (
                <div className="mt-4 text-xs text-gray-500">Semaine en cours depuis le {new Date(user.usage.weekStart).toLocaleDateString()}</div>
              )}
            </>
          ) : (
            <div className="text-sm text-gray-700">Connectez-vous pour voir votre consommation. <Link to="/login" className="text-[#2C3E50] hover:text-[#2C3E50]/80">Se connecter</Link></div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free */}
        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <h2 className="text-xl font-bold mb-2 text-[#2C3E50]">Gratuit</h2>
          <p className="text-2xl mb-4 text-[#87A96B]">0€ <span className="text-sm text-gray-600">/ mois</span></p>
          <ul className="space-y-2 mb-6 text-gray-600">
            <li>✓ 12 suggestions / semaine</li>
            <li>✓ 3 recettes / semaine</li>
            <li>✓ 3 itérations de recettes</li>
            <li>✓ 3 questions à YAQAM</li>
            <li>✓ Recettes publiques</li>
          </ul>
          <Link to="/register" className="block text-center py-2 bg-gray-100 text-[#2C3E50] rounded-lg hover:bg-gray-200 font-medium">Créer un compte</Link>
        </div>

        {/* Premium */}
        <div className="border-2 border-[#E67E22] rounded-lg p-6 bg-[#E67E22]/5 relative">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#E67E22] text-white px-4 py-1 rounded-full text-sm font-medium">Recommandé</div>
          <h2 className="text-xl font-bold mb-2 text-[#2C3E50]">Premium</h2>
          <p className="text-2xl mb-4 text-[#E67E22]">9,99€ <span className="text-sm text-gray-600">/ mois</span></p>
          <ul className="space-y-2 mb-6 text-gray-600">
            <li>✓ 120 suggestions / semaine</li>
            <li>✓ 30 recettes / semaine</li>
            <li>✓ 30 itérations de recettes</li>
            <li>✓ 30 questions à YAQAM</li>
            <li>✓ Recettes privées</li>
          </ul>
          <button onClick={handleSubscribeClick} className="w-full py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#E67E22]/90 font-medium">S'abonner</button>
        </div>

        {/* Unlimited */}
        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <h2 className="text-xl font-bold mb-2 text-[#2C3E50]">Illimité</h2>
          <p className="text-2xl mb-4 text-[#2C3E50]">Sur invitation</p>
          <ul className="space-y-2 mb-6 text-gray-600">
            <li>∞ Tout illimité</li>
            <li>✓ Recettes privées</li>
            <li>✓ Accès anticipé aux nouveautés</li>
          </ul>
          <div className="text-center text-sm text-gray-600">Réservé aux invités/amis</div>
        </div>
        </div>
      )}

      <p className="text-sm text-gray-600 mt-6">* Paiement sécurisé. Sans engagement, résiliable à tout moment via le portail de facturation.</p>
    </div>
    </>
  );
};

export default PricingPage;
